<template>
  <div class="player-analysis-view">
    <!-- League Panel -->
    <div
      v-if="!selectedTeam"
      class="panel league-panel"
    >
      <h3>Leagues</h3>
      <div
        v-for="country in countries"
        :key="country.id"
        class="list-item flag-item"
        :class="{ active: selectedCountry && selectedCountry.id === country?.id }"
        @click="selectLeague(country)"
      >
        <span>{{ country.code || country.name }}</span>
        <span>{{ country.league_name }}</span>
      </div>
    </div>

    <!-- Team Panel -->
    <div
      v-if="selectedCountry && !selectedPlayer"
      class="panel team-panel"
    >
      <div class="panel-header">
        <button @click="clearLeagueSelection" class="back-button">← Back</button>
        <h3>Teams</h3>
      </div>
      <div v-if="loadingTeams">Loading...</div>
      <div
        v-else
        v-for="team in teams"
        :key="team?.id"
        class="list-item team-item"
        :class="{ active: selectedTeam && selectedTeam.id === team?.id }"
        @click="selectTeam(team)"
      >
        <span>{{ team?.name }}</span>
      </div>
    </div>

    <!-- Player Panel -->
    <div
      v-if="selectedTeam"
      class="panel player-panel"
    >
      <div class="panel-header">
        <button @click="clearTeamSelection" class="back-button">← Back</button>
        <h3>Players</h3>
      </div>
      <div v-if="loadingPlayers">Loading...</div>
      <div
        v-else
        v-for="player in players"
        :key="player?.id"
        class="list-item player-item"
        :class="{ active: selectedPlayer && selectedPlayer.id === player?.id }"
        @click="selectPlayer(player)"
      >
        <img
          :src="selectedTeam && player ? getPlayerImageUrl(player.team_name, player.slug) : '/images/page-logo.png'"
          :alt="player?.name"
          class="logo player-photo"
          @error="(e) => onPlayerImgErrorAdvanced(e, player.team_name, player.slug)"
        />

        <span>{{ player?.name }}</span>
      </div>
    </div>

    <!-- Details Panel -->
    <div
      v-if="selectedPlayer"
      class="panel details-panel"
    >
      <div class="panel-header">
        <button @click="clearPlayerSelection" class="back-button">← Back</button>
        <h3>Player Details</h3>
      </div>
      <div v-if="loadingStats">Loading stats...</div>
      <div v-else-if="selectedPlayer && playerStats.length">
        <div class="player-bio">
          <h4>{{ playerStats[0].player_name }}</h4>
          <p>{{ playerStats[0].team_name }}</p>
          <p>
            <span v-if="playerStats[0].player?.age">Age: {{ playerStats[0].player?.age }}</span>
            <span v-if="playerStats[0].player?.date_of_birth"> | DOB: {{ playerStats[0].player?.date_of_birth }}</span>
            <span v-if="playerStats[0].player?.nationality"> | {{ playerStats[0].player?.nationality }}</span>
            <span v-if="playerStats[0].player?.height"> | {{ playerStats[0].player?.height }}cm</span>
          </p>
          <p v-if="playerStats[0].player?.positions">Position: {{ playerStats[0].player?.positions }}</p>
        </div>
        <hr />
        <table class="stats-table">
          <thead>
            <tr>
              <th>Tournament</th>
              <th>Apps</th>
              <th>Mins</th>
              <th>Goals</th>
              <th>Assists</th>
              <th>Pass %</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stat in playerStats" :key="stat?.id">
              <td>{{ stat.tournament }}</td>
              <td>{{ stat.appearances }}</td>
              <td>{{ stat.minutes_played }}</td>
              <td>{{ stat.goals }}</td>
              <td>{{ stat.assists }}</td>
              <td>{{ stat.pass_success_rate }}</td>
              <td>
                <strong>{{ stat.rating }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p>Select a player to see their stats.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { fetchCountries, fetchTeams, fetchPlayers, fetchPlayerStats } from '@/api/client.js'
// State
const countries = ref([])
const selectedCountry = ref(null)
const teams = ref([])
const selectedTeam = ref(null)
const players = ref([])
const selectedPlayer = ref(null)
const playerStats = ref([])
const loadingTeams = ref(false)
const loadingPlayers = ref(false)
const loadingStats = ref(false)

// 获取媒体基础URL
const mediaBaseUrl = import.meta.env.VITE_MEDIA_BASE_URL || 'http://localhost:8000/';

// 将字符串转换为slug格式的函数
const slugify = (value) => {
  if (!value) return '';
  // 转换为小写
  value = value.toString().toLowerCase();
  // 去除重音符号
  value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // 将非字母数字字符替换为连字符
  value = value.replace(/[^a-z0-9]+/g, '-');
  // 去除首尾连字符
  value = value.replace(/^-+|-+$/g, '');
  return value;
};

// 生成球员图片URL的函数
const getPlayerImageUrl = (teamName, playerSlug) => {
  // 使用球队名称的slug而不是display_name
  const teamSlug = slugify(teamName);
  let imageUrl = `${mediaBaseUrl}media/team_player_images/${teamSlug}/${playerSlug}.jpg`;
  return imageUrl;
};

// 更智能的图片加载失败处理
const onPlayerImgErrorAdvanced = (e, teamName, playerSlug) => {
  console.log(`图片加载失败: ${e.target.src}`);
  
  // 最终使用默认图片
  e.target.src = '/images/page-logo.png';
};

// Event Handlers & Watchers
const selectLeague = (country) => {
  selectedCountry.value = country
  console.log('Selected country:', country)
}

const selectTeam = (team) => {
  console.log(`selected team ${team}`)
  selectedTeam.value = team
}

const selectPlayer = (player) => {
  selectedPlayer.value = player
}

// 返回/清除选择的函数
const clearLeagueSelection = () => {
  selectedCountry.value = null
  teams.value = []
  selectedTeam.value = null
  players.value = []
  selectedPlayer.value = null
  playerStats.value = []
}

const clearTeamSelection = () => {
  selectedTeam.value = null
  players.value = []
  selectedPlayer.value = null
  playerStats.value = []
}

const clearPlayerSelection = () => {
  selectedPlayer.value = null
  playerStats.value = []
}

// 获取球队数据的函数
const fetchTeamsData = async (countryId) => {
  loadingTeams.value = true
  try {
    teams.value = await fetchTeams(countryId)
  } catch (e) {
    console.error(`Failed to fetch teams: ${e.message}`)
  } finally {
    loadingTeams.value = false
  }
}

// 获取球员数据的函数
const fetchPlayersData = async (teamId) => {
  loadingPlayers.value = true
  try {
    players.value = await fetchPlayers('', teamId)
  } catch (e) {
    console.error(`Failed to fetch players: ${e.message}`)
  } finally {
    loadingPlayers.value = false
  }
}

// 获取球员统计数据的函数
const fetchPlayerStatsData = async (playerId) => {
  loadingStats.value = true
  try {
    playerStats.value = await fetchPlayerStats(playerId)
  } catch (e) {
    console.error(`Failed to fetch player stats: ${e.message}`)
  } finally {
    loadingStats.value = false
  }
}

onMounted(async () => {
  try {
    let ajax_result = await  fetchCountries()
    countries.value = ajax_result
    console.log('Countries data:', countries.value)
    // Select the first country by default
    if (countries.value.length > 0) {
      selectedCountry.value = countries.value[0]
      console.log('Selected country:', selectedCountry.value)
      await fetchTeamsData(selectedCountry.value?.id)
    }else{
      console.log('No countries found')
    }
  } catch (e) {
    console.error(`Failed to fetch countries: ${e.message}`)
  }
})

const onPlayerImgError = (e) => {
  // console.log(e)
  console.log(`omitting original src: ${e.target.src}`)
  e.target.src = '/images/page-logo.png';
};

watch(selectedCountry, (newCountry) => {
  console.log('Country changed:', newCountry)
  if (newCountry) {
    fetchTeamsData(newCountry.id)
  } else {
    teams.value = []
    selectedTeam.value = null
    players.value = []
    selectedPlayer.value = null
    playerStats.value = []
  }
})

watch(selectedTeam, (newTeam) => {
  if (newTeam) {
    console.log(`fetching info about team ${newTeam} with id ${newTeam.id}`)
    fetchPlayersData(newTeam.id)
  } else {
    players.value = []
    selectedPlayer.value = null
    playerStats.value = []
  }
})

watch(selectedPlayer, (newPlayer) => {
  if (newPlayer) fetchPlayerStatsData(newPlayer.id)
})
</script>

<style scoped>
/* Styles remain the same */
.player-analysis-view {
  display: flex;
  height: calc(100vh - 120px);
  gap: 15px;
}

.panel {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
  overflow-y: auto;
}

.panel-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.back-button {
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  margin-right: 10px;
  cursor: pointer;
  font-size: 14px;
}

.back-button:hover {
  background-color: #0056b3;
}

.league-panel {
  flex: 0 0 180px;
}

.team-panel {
  flex: 1 1 250px;
}

.player-panel {
  flex: 1 1 250px;
}

.details-panel {
  flex: 2 1 500px;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;
  margin-bottom: 5px;
}

.list-item:hover {
  background-color: #eef;
}

.list-item.active {
  background-color: #dde;
  font-weight: bold;
}

.logo {
  width: 25px;
  height: 25px;
  object-fit: contain;
}

.player-photo {
  border-radius: 50%;
}

.flag-item img {
  width: 30px;
}

.player-bio {
  margin-bottom: 15px;
}

.player-bio h4 {
  margin-bottom: 5px;
}

.player-bio p {
  margin: 0;
  font-size: 0.9em;
  color: #666;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

.stats-table th,
.stats-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.stats-table thead {
  background-color: #f2f2f2;
}
</style>
